import React, { useState } from 'react';
import { Zap, Search, Users, Crown, Star, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const RoleManagementTab = ({ 
  user,
  loading, 
  setLoading,
  language,
  t,
  showSuccessToast,
  showErrorToast,
  getRoleInfo,
  loadData,
  onUserUpdate
}) => {
  // Enhanced role management state
  const [roleManagement, setRoleManagement] = useState({
    searchTerm: '',
    searchResults: [],
    selectedUser: null,
    newRole: 'player',
    reason: '',
    showRoleChangeModal: false,
    isSearching: false
  });

  // Enhanced user search for role management
  const searchUsersForRoleChange = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setRoleManagement(prev => ({ ...prev, searchResults: [], isSearching: false }));
      return;
    }

    setRoleManagement(prev => ({ ...prev, isSearching: true }));

    try {
      const response = await axios.get(`/api/users/search?q=${searchTerm}`);
      setRoleManagement(prev => ({ 
        ...prev, 
        searchResults: response.data.filter(u => u.role !== 'main_admin'), // Can't change main admin role
        isSearching: false 
      }));
    } catch (error) {
      console.error('Search users error:', error);
      setRoleManagement(prev => ({ ...prev, searchResults: [], isSearching: false }));
      showErrorToast(t('failedToSearchUsers', language));
    }
  };

  // Handle role change
  const handleRoleChange = async (userData, newRole, reason) => {
    if (!userData || !newRole) {
      showErrorToast(t('invalidUserOrRole', language));
      return;
    }

    if (userData.role === newRole) {
      showErrorToast(t('userAlreadyHasRole', language));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`/api/admin/users/${userData._id}/role`, {
        role: newRole,
        reason: reason || t('roleChangeDescription', language)
      });

      showSuccessToast(t('roleChangedSuccessfully', language, { username: userData.inGameName, role: newRole }));
      
      // Reset role management state
      setRoleManagement({
        searchTerm: '',
        searchResults: [],
        selectedUser: null,
        newRole: 'player',
        reason: '',
        showRoleChangeModal: false,
        isSearching: false
      });
      
      // Reload data
      loadData();
      
      // Emit real-time update if available
      if (response.data.user) {
        console.log('Role changed successfully:', response.data.user);
      }

    } catch (error) {
      const message = error.response?.data?.message || t('failedToChangeUserRole', language);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const searchTerm = e.target.value;
    setRoleManagement(prev => ({ ...prev, searchTerm }));
    searchUsersForRoleChange(searchTerm);
  };

  const selectUser = (user) => {
    setRoleManagement(prev => ({
      ...prev,
      selectedUser: user,
      searchResults: [],
      searchTerm: user.inGameName,
      newRole: user.role
    }));
  };

  const openRoleChangeModal = () => {
    if (!roleManagement.selectedUser) {
      toast.error(t('pleaseSelectUser', language));
      return;
    }
    if (roleManagement.selectedUser.role === roleManagement.newRole) {
      toast.error(t('userAlreadyHasRole', language));
      return;
    }
    setRoleManagement(prev => ({ ...prev, showRoleChangeModal: true }));
  };

  return (
    <div className="role-management">
      <div className="role-management-section">
        <h4 className="role-management-title">
          <Zap className="w-5 h-5" />
          {t('userRoleManagement', language)}
        </h4>
        <p className="role-management-description">{t('searchUsersModifyRoles', language)}</p>
        
        <div className="role-management-grid">
          {/* Search Section */}
          <div className="role-search-section">
            <div>
              <label className="role-search-label">
                {t('searchUsers', language)}
              </label>
              <div className="role-search-container">
                <Search className="role-search-icon" />
                <input
                  type="text"
                  placeholder={t('searchByUsername', language)}
                  value={roleManagement.searchTerm}
                  onChange={handleSearchChange}
                  className="role-search-input"
                />
                {roleManagement.isSearching && (
                  <div className="role-search-spinner">
                    <div className="search-spinner"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Search Results */}
            {roleManagement.searchResults.length > 0 && (
              <div className="role-search-results">
                <div className="role-search-results-header">
                  <p className="role-search-results-title">{t('searchResults', language)} ({roleManagement.searchResults.length})</p>
                </div>
                {roleManagement.searchResults.map(user => {
                  const roleInfo = getRoleInfo(user.role);
                  return (
                    <div
                      key={user._id}
                      onClick={() => selectUser(user)}
                      className="role-search-result-item"
                    >
                      <div className="role-search-result-content">
                        <div className="role-search-result-info">
                          <p className="role-search-result-name">{user.inGameName}</p>
                          <p className="role-search-result-email">{user.email}</p>
                          <p className="role-search-result-stats">{user.points} {t('points', language)} • {user.wins}W-{user.losses}L</p>
                        </div>
                        <div className={`role-badge ${roleInfo.className}`}>
                          {roleInfo.icon}
                          <span>{roleInfo.display}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Selected User & Role Change Section */}
          <div className="role-selection-section">
            {roleManagement.selectedUser ? (
              <>
                <div className="selected-user-card">
                  <h5 className="selected-user-title">{t('selectedUser', language)}</h5>
                  <div className="selected-user-info">
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('username', language)}:</span> 
                      <span className="selected-user-info-value">{roleManagement.selectedUser.username}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('inGameName', language)}:</span> 
                      <span className="selected-user-info-value">{roleManagement.selectedUser.inGameName}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('email', language)}:</span> 
                      <span className="selected-user-info-value">{roleManagement.selectedUser.email}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('points', language)}:</span> 
                      <span className="selected-user-info-value points">{roleManagement.selectedUser.points}</span>
                    </div>
                    <div className="selected-user-info-item">
                      <span className="selected-user-info-label">{t('currentRole', language)}:</span>
                      <span className={`role-badge ${getRoleInfo(roleManagement.selectedUser.role).className}`}>
                        {getRoleInfo(roleManagement.selectedUser.role).display}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="role-select-group">
                  <label className="role-select-label">
                    {t('newRole', language)}
                  </label>
                  <select
                    value={roleManagement.newRole}
                    onChange={(e) => setRoleManagement(prev => ({ ...prev, newRole: e.target.value }))}
                    className="role-select"
                  >
                    <option value="player">{t('player', language)}</option>
                    <option value="verified_sponsor">{t('verifiedSponsor', language) || 'Verified Sponsor'}</option>
                    <option value="approver">{t('approver', language)}</option>
                    <option value="supervisor">{t('supervisor', language)}</option>
                    <option value="speedos">{t("speedos", language) || "Speedos"}</option>
                    <option value="ryder">{t("ryder", language) || "Ryder"}</option>
                    {user.role === 'main_admin' && (
                      <option value="manager">{t('manager', language) || 'Manager'}</option>
                    )}
                  </select>
                  
                  {roleManagement.newRole !== roleManagement.selectedUser.role && (
                    <div className="role-change-preview">
                      <p className="role-change-preview-text">
                        <strong>{t('roleChangePreview', language)}</strong> {getRoleInfo(roleManagement.selectedUser.role).display} → {getRoleInfo(roleManagement.newRole).display}
                      </p>
                      <p className="role-change-preview-description">
                        {getRoleInfo(roleManagement.newRole).description}
                      </p>
                    </div>
                  )}
                </div>

                <div className="role-reason-group">
                  <label className="role-reason-label">
                    {t('reasonForChange', language)}
                  </label>
                  <textarea
                    value={roleManagement.reason}
                    onChange={(e) => setRoleManagement(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder={t('enterReasonForRoleChange', language)}
                    className="role-reason-textarea"
                    rows={3}
                  />
                </div>

                <button
                  onClick={openRoleChangeModal}
                  disabled={loading || roleManagement.selectedUser.role === roleManagement.newRole}
                  className="role-change-btn"
                >
                  {loading ? t('processing', language) : t('changeUserRole', language)}
                </button>
              </>
            ) : (
              <div className="role-placeholder">
                <Users className="role-placeholder-icon" />
                <p className="role-placeholder-text">{t('searchAndSelectUser', language)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Role Information Guide */}
      <div className="role-guide">
        <h5 className="role-guide-title">{t('rolePermissionsGuide', language)}</h5>
        <div className="role-guide-grid">
          {['main_admin', 'manager', 'supervisor', 'approver', 'verified_sponsor', 'speedos', 'ryder', 'player'].map(role => {
            const roleInfo = getRoleInfo(role);
            return (
              <div key={role} className={`role-guide-item ${roleInfo.className}`}>
                <div className="role-guide-header">
                  {roleInfo.icon}
                  <h6 className="role-guide-name">{roleInfo.display}</h6>
                </div>
                <p className="role-guide-description">{roleInfo.description}</p>
                {role === 'main_admin' && (
                  <p className="role-guide-warning">{t('cannotBeChanged', language)}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Role Change Confirmation Modal */}
      {roleManagement.showRoleChangeModal && (
        <RoleChangeConfirmationModal
          roleManagement={roleManagement}
          setRoleManagement={setRoleManagement}
          handleRoleChange={handleRoleChange}
          getRoleInfo={getRoleInfo}
          loading={loading}
          language={language}
          t={t}
        />
      )}
    </div>
  );
};

// Role Change Confirmation Modal
const RoleChangeConfirmationModal = ({ 
  roleManagement, 
  setRoleManagement, 
  handleRoleChange, 
  getRoleInfo, 
  loading,
  language,
  t
}) => {
  const currentRoleInfo = getRoleInfo(roleManagement.selectedUser.role);
  const newRoleInfo = getRoleInfo(roleManagement.newRole);

  const confirmRoleChange = () => {
    handleRoleChange(
      roleManagement.selectedUser,
      roleManagement.newRole,
      roleManagement.reason
    );
  };

  const closeModal = () => {
    setRoleManagement(prev => ({ ...prev, showRoleChangeModal: false }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-icon">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h3 className="modal-title">{t('confirmRoleChange', language)}</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-user-info">
            <p className="modal-user-name">{roleManagement.selectedUser.inGameName}</p>
            <p className="modal-user-email">{roleManagement.selectedUser.email}</p>
          </div>

          <div className="modal-role-change">
            <div className={`role-badge ${currentRoleInfo.className}`}>
              {currentRoleInfo.icon}
              <span>{currentRoleInfo.display}</span>
            </div>
            <div className="modal-role-arrow">→</div>
            <div className={`role-badge ${newRoleInfo.className}`}>
              {newRoleInfo.icon}
              <span>{newRoleInfo.display}</span>
            </div>
          </div>

          {roleManagement.reason && (
            <div className="modal-reason">
              <p className="modal-reason-title">{t('reason', language)}:</p>
              <p className="modal-reason-text">{roleManagement.reason}</p>
            </div>
          )}

          <div className="modal-warning">
            <p className="modal-warning-text">
              {t('thisActionWillImmediately', language) || 'This action will immediately change the user\'s role and permissions.'}
            </p>
          </div>
        </div>
        
        <div className="modal-actions">
          <button
            onClick={confirmRoleChange}
            disabled={loading}
            className="modal-btn modal-btn-primary"
          >
            {loading ? t('changingRole', language) : t('confirmChange', language)}
          </button>
          <button
            onClick={closeModal}
            disabled={loading}
            className="modal-btn modal-btn-secondary"
          >
            {t('cancel', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleManagementTab;